import { type PlanId, PLAN_NODE_IDS } from '@/lib/branchTypes'
import { type PerspectiveCategory } from '@/app/common/blocks/perspectiveTabContext'
import {
  EPILOGUE_HEADING_ID,
  POV_HEADING_IDS,
  type TimelineBranchpointEntry,
  type TimelineEndpointEntry,
} from './planATimelineDiagramModel'

// Plan switch remounts the tree + epilogue state lands a commit
// later, so targets may not exist on the click frame: poll first.

const SCROLL_OFFSET_PX = 80

// ~0.6s at 60fps; missing target fails quietly, doesn't hang.
const MAX_WAIT_FRAMES = 40

const restingScrollTop = (el: HTMLElement, placement: 'top' | 'center') => {
  const elTop = el.getBoundingClientRect().top + window.scrollY
  const offset = placement === 'center' ? window.innerHeight / 2 : SCROLL_OFFSET_PX
  return Math.max(0, elTop - offset)
}

// Settle-then-smooth: wait until the resting top holds steady (reflow
// done), then one smooth scroll, so we don't chase a moving target.
const STABLE_FRAMES_REQUIRED = 6
const STABILIZE_MAX_FRAMES = 120 // ~2s at 60fps
const scrollElementIntoView = (el: HTMLElement, placement: 'top' | 'center' = 'top') => {
  let lastTop = Number.NaN
  let stableFrames = 0
  let frames = 0
  const tick = () => {
    const target = restingScrollTop(el, placement)
    stableFrames = Math.abs(target - lastTop) < 1 ? stableFrames + 1 : 0
    lastTop = target
    const settled = stableFrames >= STABLE_FRAMES_REQUIRED
    if (settled || ++frames >= STABILIZE_MAX_FRAMES) {
      if (Math.abs(window.scrollY - target) > 1) {
        window.scrollTo({ top: target, behavior: 'smooth' })
      }
      return
    }
    requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

// Poll for `#targetId` across frames; no-op if it never appears.
const waitForElement = (targetId: string, act: (el: HTMLElement) => void) => {
  let frames = 0
  const tick = () => {
    const el = document.getElementById(targetId)
    if (el) {
      act(el)
      return
    }
    if (++frames < MAX_WAIT_FRAMES) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

export type DiagramNavigation = {
  plan: PlanId
  scrollToId?: string
  expandDetails?: boolean
  // Branchpoint box opens on hashchange, not a DOM attr (see expandBranchpointByHash).
  expandBranchpoint?: boolean
  setEpilogueExpanded?: (expanded: boolean) => void
  epilogueExpanded?: boolean
  // Defaults to scrollToId for branchpoint/epilogue; null skips.
  updateHashTo?: string | null
}

// On a `?perspective=` page the playbook (+ hash deep-link machinery)
// isn't mounted; registered by useStandaloneDiagramProps so the
// rest of the module stays router-free.
let escapePerspectiveNavigator: ((targetHash: string) => void) | null = null
export const setEscapePerspectiveNavigator = (fn: ((targetHash: string) => void) | null) => {
  escapePerspectiveNavigator = fn
}
const isOnPerspectivePage = (): boolean =>
  typeof window !== 'undefined' &&
  window.location.pathname.startsWith('/perspective/')

// Exported for nav buttons outside this module (e.g. ScenarioEndNavGrid's
// "Choose another Plan") that target playbook elements: returns true when it
// handled the jump by leaving the `?perspective=` page for the main one.
export const escapePerspectiveIfNeeded = (targetHash: string): boolean => {
  if (typeof window === 'undefined') return false
  if (!isOnPerspectivePage()) return false
  // No navigator (diagram outside playbook): full nav still clears `perspective`.
  if (escapePerspectiveNavigator) {
    escapePerspectiveNavigator(targetHash)
  } else {
    // Leave /perspective/[category] for the main playbook, carrying the hash.
    window.location.assign(`/#${targetHash}`)
  }
  return true
}

// replaceState so we don't fire the playbook's hashchange (it would fight our scroll).
const writeHash = (id: string) => {
  const url = new URL(window.location.href)
  url.hash = id
  window.history.replaceState(window.history.state, '', url.toString())
}

// BranchpointBox expands on the hashchange the assignment emits;
// a box mounted later (plan-switch remount) reads the same hash.
const expandBranchpointByHash = (id: string) => {
  if (window.location.hash === `#${id}`) {
    // Re-click: same hash won't re-fire hashchange, so nudge it manually.
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  } else {
    window.location.hash = id
  }
}

// `currentPlan` lets us skip the switch (and remount) when already there.
export const runDiagramNavigation = (
  nav: DiagramNavigation,
  currentPlan: PlanId | null,
  selectPlan: (planId: PlanId) => void,
) => {
  const targetId = nav.scrollToId ?? PLAN_NODE_IDS[nav.plan]
  const hashTarget = nav.updateHashTo === undefined ? nav.scrollToId : nav.updateHashTo

  // On a perspective page the playbook isn't mounted; route to the hash and bail.
  if (escapePerspectiveIfNeeded(targetId)) return

  if (currentPlan !== nav.plan) selectPlan(nav.plan)
  if (nav.setEpilogueExpanded) nav.setEpilogueExpanded(nav.epilogueExpanded ?? false)

  // Branchpoint expand + scroll runs entirely via the hash machinery; our own
  // scroll loop would fight it, so hand off and return.
  if (nav.expandBranchpoint && nav.scrollToId) {
    // Poll: box may be mid-remount, so a late hashchange lands on a mounted listener.
    waitForElement(nav.scrollToId, () => expandBranchpointByHash(nav.scrollToId!))
    return
  }

  waitForElement(targetId, (el) => {
    if (nav.expandDetails && el instanceof HTMLDetailsElement) el.open = true
    if (hashTarget) writeHash(hashTarget)
    scrollElementIntoView(el)
  })
}

export type DiagramEndpointDeps = {
  setEpilogueExpanded: (expanded: boolean) => void
  setPerspective: (category: PerspectiveCategory) => void
}

export const runDiagramEndpointNavigation = (
  entry: TimelineEndpointEntry,
  deps: DiagramEndpointDeps,
) => {
  if (entry.kind === 'epilogue') {
    // The epilogue always expands IN PLACE: every control firing this entry
    // (nav grid / end-cap diagram) renders directly above the mounted ending
    // bodies, so route through the hash exactly like the POVs below —
    // useAnchorDeepLink mounts the body and nudges the scroll gently. No
    // plan switch and no scroll-to-target of our own: those fight the
    // in-place height animation. On a `?perspective=` page the playbook
    // isn't mounted, so escape to the main page carrying the hash instead.
    if (escapePerspectiveIfNeeded(EPILOGUE_HEADING_ID)) return
    expandEndingByHash(EPILOGUE_HEADING_ID)
    return
  }
  // Collapse the epilogue: endings share one slot below the diagram.
  deps.setEpilogueExpanded(false)
  runDiagramPerspectiveNavigation(entry.perspective, deps.setPerspective)
}

// Map a branchpoint entry's `kind` to the right expand mechanism.
export const branchpointNavOpts = (
  entry: TimelineBranchpointEntry,
): { scrollToId: string; expandDetails?: boolean; expandBranchpoint?: boolean } =>
  entry.kind === 'branchpoint'
    ? { scrollToId: entry.detailsId, expandBranchpoint: true }
    : { scrollToId: entry.detailsId, expandDetails: true }

// POV opens in place via the POV hash (deep-link handler mounts + scrolls);
// on a `?perspective=` flat page the playbook isn't mounted, so swap the page.
export const runDiagramPerspectiveNavigation = (
  perspective: PerspectiveCategory,
  setPerspective: (category: PerspectiveCategory) => void,
) => {
  const povHeadingId =
    perspective === 'mainScenario' ? undefined : POV_HEADING_IDS[perspective]
  if (povHeadingId && !isOnPerspectivePage()) {
    expandEndingByHash(povHeadingId)
    return
  }
  setPerspective(perspective)
}

// Toggle-off for an open ending (nav-grid re-click). NOT a `location.hash`
// assignment: navigating to an empty fragment makes the browser jump-scroll
// to the top of the document. replaceState clears the hash without touching
// scroll, but fires no hashchange — so dispatch it manually: useSelectedEnding
// drops the highlight and useAnchorDeepLink's onCollapseEndings animates the
// open ending back to height 0, leaving the reader exactly where they are.
export const closeSelectedEnding = () => {
  const url = new URL(window.location.href)
  url.hash = ''
  window.history.replaceState(window.history.state, '', url.toString())
  window.dispatchEvent(new HashChangeEvent('hashchange'))
}

// Open an ending body (epilogue or POV) via the hash. NOT a `location.hash`
// assignment: the ending bodies are pre-mounted (at height 0), so the
// browser's native fragment navigation would instantly snap-scroll to the
// hidden element. pushState writes the hash without any native scroll —
// keeping a history entry so Back still closes the ending — and the manual
// dispatch drives the same hashchange machinery a real assignment would.
const expandEndingByHash = (id: string) => {
  if (window.location.hash !== `#${id}`) {
    const url = new URL(window.location.href)
    url.hash = id
    window.history.pushState(window.history.state, '', url.toString())
  }
  window.dispatchEvent(new HashChangeEvent('hashchange'))
}
